import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Collapse,
  Button,
  ButtonGroup,
  Input,
  InputGroup,
  InputGroupText
} from 'reactstrap';
import { getContentExtra, removeContentExtra, updateContentExtra, addChapterToSubcollection, getContentExtraWithChapters, updateChapterInSubcollection, createContentExtra, getContentChapters, generateAudioForChapter, testSimpleAudioGeneration, manuallyUpdateChapterAudio, getExpectedAudioPaths } from '../../helpers/API/contentExtra';
import i18n from '../../i18n';
import { appLanguages } from '../../common/languages';
import { langFlags } from '../../common/langFlags';
import NewContentForm from './NewContentForm';
import { NEVILLE_GODDARD_VOICE } from '../../constants/nevilleGoddard';
import { translateContentWithStructure } from '../../helpers/API/content/chatGPT';

// Multi-chunk audio player component
const MultiChunkAudioPlayer = ({ chunkUrls, chapterTitle }) => {
  const [currentChunk, setCurrentChunk] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const playNext = () => {
    if (currentChunk < chunkUrls.length - 1) {
      setCurrentChunk(currentChunk + 1);
    } else {
      setCurrentChunk(0); // Loop back to start
      setIsPlaying(false);
    }
  };

  const playPrevious = () => {
    if (currentChunk > 0) {
      setCurrentChunk(currentChunk - 1);
    }
  };

  useEffect(() => {
    if (audioRef.current && isPlaying) {
      audioRef.current.play();
    }
  }, [currentChunk]);

  return (
    <div style={{
      border: '1px solid #ddd',
      borderRadius: 6,
      padding: 12,
      backgroundColor: '#fafafa'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
        fontSize: '12px',
        color: '#666'
      }}>
        <span>🧩 Multi-chunk audio ({chunkUrls.length} parts)</span>
        <span>Part {currentChunk + 1} of {chunkUrls.length}</span>
      </div>

      <audio
        ref={audioRef}
        controls
        src={chunkUrls[currentChunk]}
        style={{ width: '100%', height: '32px' }}
        onEnded={playNext}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      >
        Your browser does not support the audio element.
      </audio>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 8,
        marginTop: 8
      }}>
        <button
          onClick={playPrevious}
          disabled={currentChunk === 0}
          style={{
            padding: '4px 8px',
            fontSize: '12px',
            border: '1px solid #ccc',
            borderRadius: 4,
            backgroundColor: currentChunk === 0 ? '#f5f5f5' : '#fff',
            color: currentChunk === 0 ? '#999' : '#333',
            cursor: currentChunk === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          ← Previous
        </button>
        <button
          onClick={playNext}
          style={{
            padding: '4px 8px',
            fontSize: '12px',
            border: '1px solid #ccc',
            borderRadius: 4,
            backgroundColor: '#fff',
            color: '#333',
            cursor: 'pointer'
          }}
        >
          {currentChunk === chunkUrls.length - 1 ? 'Restart' : 'Next →'}
        </button>
      </div>
    </div>
  );
};

const ContentTable = ({ section, language, voices }) => {
  const [content, setContent] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [collapse, setCollapse] = useState([]);
  const [translating, setTranslating] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [generatingAudio, setGeneratingAudio] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // State for adding a new chapter (only for lectures/books)
  const [chapterFormItemId, setChapterFormItemId] = useState(null);
  const [chapterFormValues, setChapterFormValues] = useState({
    title: '',
    text: ''
  });

  // State for editing existing chapters
  const [editingChapter, setEditingChapter] = useState(null);
  const [editChapterValues, setEditChapterValues] = useState({
    title: '',
    text: []
  });
  const [editingChapterLoading, setEditingChapterLoading] = useState(false);
  const [useUnifiedTextEditor, setUseUnifiedTextEditor] = useState(false);
  const [creatingChapter, setCreatingChapter] = useState(false);
  const [audioGeneratingChapters, setAudioGeneratingChapters] = useState(new Set());
  const [generatingAudioChapters, setGeneratingAudioChapters] = useState(new Set());
  const [generatingBulkAudio, setGeneratingBulkAudio] = useState(null);

  // Track which chapters (per parent item) are expanded.
  // Structure: { [parentItemId]: { [chapterId]: true/false } }
  const [expandedChapters, setExpandedChapters] = useState({});

  // Helper: Toggle expansion for a given chapter.
  const toggleChapterExpansion = (parentId, chapterId) => {
    setExpandedChapters(prev => {
      const parentChapters = prev[parentId] || {};
      return {
        ...prev,
        [parentId]: {
          ...parentChapters,
          [chapterId]: !parentChapters[chapterId]
        }
      };
    });
  };

  // Fetch all content for the given section and language.
  const fetchContent = useCallback(async () => {
    setIsLoading(true);
    // Use the new function that includes subcollection chapters
    const data = await getContentExtraWithChapters(section, language);

    // Debug: Log the fetched data
    console.log(`🔍 DEBUG: Fetched ${data.length} items for ${section}/${language}:`, data.map(item => ({
      id: item.id,
      title: item.title,
      chaptersCount: item.chapters?.length || 0,
      chaptersArray: item.chapters
    })));

    setContent(data);
    setCollapse(Array(data.length).fill(false));
    setIsLoading(false);
  }, [section, language]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Toggle collapse state for a given card.
  const toggleCollapse = (index) => {
    setCollapse(prev => {
      const newCollapse = [...prev];
      newCollapse[index] = !newCollapse[index];
      return newCollapse;
    });
  };

  // Complete translation function with chapter support and TTS generation
  const handleTranslate = async (item, targetLang) => {
    const key = `${item.id}-${targetLang}`;
    setTranslating(prev => ({ ...prev, [key]: true }));

    try {
      console.log(`🌍 Starting translation of "${item.title}" (${item.id}) to ${targetLang}...`);

      // Handle legacy lectures that don't have voice field
      const sourceVoice = item.voice || (section === 'lectures' || section === 'books' ? NEVILLE_GODDARD_VOICE.id : null);
      console.log(`🎙️ Using voice: ${sourceVoice} ${!item.voice ? '(default for legacy content)' : '(from original)'}`);

      // Step 1: Translate main content (title, author, fullText if exists)
      console.log('📝 Step 1: Translating main content...');

      const textsToTranslate = [];
      if (item.fullText) {
        textsToTranslate.push(item.fullText);
      } else if (item.text && Array.isArray(item.text)) {
        textsToTranslate.push(...item.text);
      }

      // Translate title and main text
      const translatedContent = await translateContentWithStructure(
        item.title,
        textsToTranslate,
        targetLang
      );

      if (!translatedContent) {
        throw new Error('Failed to translate main content');
      }

      // Translate author if needed
      let translatedAuthor = item.author;
      if (item.author && item.author !== 'Neville Goddard') {
        const authorTranslation = await translateContentWithStructure(
          item.author,
          [],
          targetLang
        );
        translatedAuthor = authorTranslation?.title || item.author;
      }

      console.log('✅ Main content translated successfully');

      // Step 2: Create the new content item in target language
      console.log('📚 Step 2: Creating content item in target language...');

            // Create new content data with consistent structure
      const newContentData = {
        ...item,
        lang: targetLang,
        title: translatedContent.title,
        author: translatedAuthor,
        // Use text array format consistently (if original has text content in main document)
        text: (item.fullText || item.text) ? translatedContent.texts : undefined,
        // Handle voice field - use original or default to Neville Goddard for lectures/books
        voice: item.voice || (section === 'lectures' || section === 'books' ? NEVILLE_GODDARD_VOICE.id : undefined),
        // Generate new ID for target language
        createdAt: new Date().toISOString(),
        translatedFrom: {
          sourceLanguage: language,
          sourceId: item.id,
          translatedAt: new Date().toISOString()
        }
      };

      // Clean up - remove old fullText field and chapters array (chapters go in subcollection)
      delete newContentData.fullText;
      delete newContentData.chapters;

      // Remove undefined text field if not needed
      if (!newContentData.text) {
        delete newContentData.text;
      }

      console.log('📦 Translated content ready with voice:', newContentData.voice);

      // Create the new content item with the same ID as source to maintain consistency
      const newContentResponse = await createContentExtra(section, targetLang, newContentData, item.id);
      const newContentId = newContentResponse.id;
      console.log(`✅ Created new content item with preserved ID: ${newContentId} (same as source)`);

            // Step 3: Fetch and translate chapters from subcollection (for lectures/books)
      let originalChapters = null;
      if (section === 'lectures' || section === 'books') {
        console.log('📖 Step 3: Fetching chapters from subcollection...');

        try {
          // Fetch chapters from original content's subcollection
          originalChapters = await getContentChapters(section, language, item.id);

          if (originalChapters && originalChapters.length > 0) {
            console.log(`📚 Found ${originalChapters.length} chapters to translate`);

            for (let i = 0; i < originalChapters.length; i++) {
              const chapter = originalChapters[i];
              console.log(`📄 Translating chapter ${i + 1}: "${chapter.title}"`);

              try {
                // Prepare chapter texts for translation
                const chapterTexts = Array.isArray(chapter.text) ? chapter.text : [chapter.text || ''];

                // Translate chapter title and text
                const translatedChapter = await translateContentWithStructure(
                  chapter.title || `Chapter ${chapter.id}`,
                  chapterTexts,
                  targetLang
                );

                if (!translatedChapter) {
                  console.error(`❌ Failed to translate chapter ${chapter.id}`);
                  continue;
                }

                // Create chapter data
                const chapterData = {
                  title: translatedChapter.title,
                  text: translatedChapter.texts
                  // No audioURL - triggers automatic TTS generation
                };

                console.log(`🔊 Adding chapter ${i + 1} with automatic TTS generation...`);

                // Add chapter to subcollection with automatic TTS
                await addChapterToSubcollection(
                  section,
                  targetLang,
                  newContentId,
                  chapterData,
                  NEVILLE_GODDARD_VOICE.id // Use Neville Goddard voice for TTS
                );

                console.log(`✅ Chapter ${i + 1} translated and audio generation started`);

                // Small delay to avoid overwhelming the TTS service
                await new Promise(resolve => setTimeout(resolve, 1500));

              } catch (chapterError) {
                console.error(`❌ Error translating chapter ${chapter.id}:`, chapterError);
              }
            }

            console.log('🎉 All chapters translated and audio generation initiated!');
          } else {
            console.log('📝 No chapters found - this content has text in main document only');
          }
        } catch (fetchError) {
          console.error('❌ Error fetching original chapters:', fetchError);
        }
      }

      // Step 4: Refresh content to show the new translated item
      console.log('🔄 Refreshing content list...');
      await fetchContent();

      // Success message
      const successMessage = originalChapters && originalChapters.length > 0
        ? `🎉 Translation completed! Created "${translatedContent.title}" with ${originalChapters.length} chapters and TTS audio generation.`
        : `🎉 Translation completed! Created "${translatedContent.title}" successfully.`;

      setErrorMessage(successMessage);
      setTimeout(() => setErrorMessage(''), 8000);

      console.log(`🌟 Translation completed successfully for "${item.title}" -> ${targetLang}`);

    } catch (error) {
      console.error('❌ Translation error:', error);
      const errorMsg = `Failed to translate "${item.title}" to ${targetLang}: ${error.message}`;
      setErrorMessage(errorMsg);
      setTimeout(() => setErrorMessage(''), 12000);
    } finally {
      setTranslating(prev => ({ ...prev, [key]: false }));
    }
  };

  // Delete a content item.
  const handleDelete = async (item) => {
    if (window.confirm(i18n.t('confirmDelete'))) {
      try {
        await removeContentExtra(section, language, item.id);
        fetchContent();
      } catch (error) {
        console.error('Delete error:', error);
      }
    }
  };

  // Regenerate the overall audio sample (for content with text in main document).
  const handleRegenerateAudio = async (item, voice) => {
    try {
      setGeneratingAudio(voice.name);
      // Use text array consistently - join paragraphs with spaces
      const text = item.text && Array.isArray(item.text)
        ? item.text.join(' ')
        : '';

      if (!text) {
        setErrorMessage('No text content available for audio regeneration. This content uses chapter-based structure.');
        setTimeout(() => setErrorMessage(''), 5000);
        return;
      }

      // (Call your regeneration API here.)
      await new Promise(resolve => setTimeout(resolve, 2000));
      fetchContent();
    } catch (error) {
      console.error('Error regenerating audio sample:', error);
      setErrorMessage('Error regenerating audio sample.');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setGeneratingAudio(null);
    }
  };

  // Parse text into array based on bullet points or line breaks
  const parseTextToArray = (text) => {
    console.log('🔍 parseTextToArray input:', { text, typeof: typeof text, length: text?.length });

    if (!text) {
      console.log('❌ No text provided, returning empty array');
      return [];
    }

    // Split by bullet points (•, -, *, numbers followed by .) or double line breaks
    const lines = text
      .split(/(?:\r?\n){2,}|(?:\r?\n)(?=[-•*]|\d+\.)/)
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => line.replace(/^[-•*]\s*|\d+\.\s*/, '').trim());

    const result = lines.length > 0 ? lines : [text.trim()];
    console.log('✅ parseTextToArray result:', { result, length: result.length });

    return result;
  };

  // Generate missing audio for all chapters in a lecture
  const handleGenerateMissingAudio = async (item) => {
    if (!item.chapters || item.chapters.length === 0) {
      setErrorMessage('No chapters found to generate audio for.');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    try {
      setGeneratingBulkAudio(item.id);
      setErrorMessage('');

      console.log(`🎙️ Starting bulk audio generation for "${item.title}" (${item.chapters.length} chapters)`);

      let generated = 0;
      let skipped = 0;
      let failed = 0;

      for (let i = 0; i < item.chapters.length; i++) {
        const chapter = item.chapters[i];

        try {
          // Check if chapter needs audio generation
          const needsAudio = !chapter.audioURL ||
                           chapter.audioError ||
                           chapter.audioErrorMessage === 'deadline-exceeded' ||
                           chapter.audioErrorMessage === 'Response is missing data field';

          if (!needsAudio) {
            console.log(`⏭️ Chapter ${chapter.id}: Already has audio, skipping`);
            skipped++;
            continue;
          }

          console.log(`🔊 Chapter ${chapter.id}: Generating audio...`);

          // Use Neville Goddard voice
          const voiceToUse = NEVILLE_GODDARD_VOICE.id;

          // Generate audio for this chapter
          await generateAudioForChapter(section, language, item.id, chapter.id, voiceToUse);

          console.log(`✅ Chapter ${chapter.id}: Audio generation started`);
          generated++;

          // Add delay between requests to avoid rate limiting
          if (i < item.chapters.length - 1) {
            console.log('⏳ Waiting 2 seconds before next chapter...');
            await new Promise(resolve => setTimeout(resolve, 2000));
          }

        } catch (chapterError) {
          console.error(`❌ Chapter ${chapter.id}: Audio generation failed:`, chapterError);

          // Don't treat deadline-exceeded as a real error
          if (chapterError.message && chapterError.message.includes('deadline-exceeded')) {
            console.log(`⏳ Chapter ${chapter.id}: Deadline exceeded (common, will retry later)`);
          } else {
            console.error(`💥 Chapter ${chapter.id}: Real error:`, chapterError);
            failed++;
          }
        }
      }

      // Success message
      const parts = [];
      if (generated > 0) parts.push(`✅ Generated: ${generated}`);
      if (skipped > 0) parts.push(`⏭️ Skipped: ${skipped}`);
      if (failed > 0) parts.push(`❌ Failed: ${failed}`);

      const message = `🎙️ Bulk audio generation completed! ${parts.join(', ')}`;
      setErrorMessage(message);
      setTimeout(() => setErrorMessage(''), 8000);

      // Refresh content to show updated status
      await fetchContent();

    } catch (error) {
      console.error('❌ Bulk audio generation error:', error);
      setErrorMessage(`Failed to generate missing audio: ${error.message}`);
      setTimeout(() => setErrorMessage(''), 8000);
    } finally {
      setGeneratingBulkAudio(null);
    }
  };

  // Handle adding a new chapter to existing content
  const handleAddChapter = async (item) => {
    if (!chapterFormValues.title.trim()) {
      setErrorMessage(i18n.t('chapterTitleRequired') || 'Chapter title is required');
      return;
    }

    if (!chapterFormValues.text.trim()) {
      setErrorMessage(i18n.t('chapterTextRequired') || 'Chapter text is required');
      return;
    }

    try {
      setCreatingChapter(true);
      setErrorMessage('');

      // Use Neville Goddard voice for automatic TTS generation
      const voiceToUse = NEVILLE_GODDARD_VOICE.id;

      // Parse text into array
      const textArray = parseTextToArray(chapterFormValues.text);

      // Prepare chapter data (no audioURL = auto-generate with TTS)
      const chapterData = {
        title: chapterFormValues.title.trim(),
        text: textArray
        // No audioURL - this triggers automatic TTS generation in addChapterToSubcollection
      };

      console.log('🔍 DEBUGGING CHAPTER CREATION:');
      console.log('📝 Original text input:', chapterFormValues.text);
      console.log('📊 Parsed text array:', textArray);
      console.log('📦 Chapter data being sent:', chapterData);
      console.log('🎙️ Voice to use:', voiceToUse);

      // Add chapter to subcollection (now returns immediately)
      const newChapter = await addChapterToSubcollection(
        section,
        language,
        item.id,
        chapterData,
        voiceToUse
      );

      // Track this chapter for audio generation
      const chapterKey = `${item.id}-${newChapter.id}`;
      setAudioGeneratingChapters(prev => new Set([...prev, chapterKey]));

      // Reset form and hide it
      setChapterFormValues({
        title: '',
        text: ''
      });
      setChapterFormItemId(null);

      // Show success message briefly
      const successMessage = `✅ Chapter "${chapterData.title}" created successfully! Audio is generating in background...`;
      console.log(successMessage);

      // Refresh content to show new chapter immediately
      await fetchContent();

      console.log('✅ Chapter created immediately, audio generating in background');

                      // Refresh content immediately after synchronous generation
        await fetchContent();

        // Clear generating state
        setAudioGeneratingChapters(prev => {
          const newSet = new Set(prev);
          newSet.delete(chapterKey);
          return newSet;
        });

    } catch (error) {
      console.error('Error adding chapter:', error);
      setErrorMessage(i18n.t('failedToAddChapter') || 'Failed to add chapter');
    } finally {
      setCreatingChapter(false);
    }
  };

  // Polling disabled - our chunked generation is synchronous
  const pollForAudioCompletion = (contentId, chapterId, chapterKey) => {
    console.log('🚫 Polling disabled - using synchronous generation');
    // No polling needed anymore
  };

  // Handle generating audio for existing chapter
  const handleGenerateChapterAudio = async (chapter, parentItem) => {
    const chapterKey = `${parentItem.id}-${chapter.id}`;

    try {
      setGeneratingAudioChapters(prev => new Set([...prev, chapterKey]));
      setErrorMessage('');

      console.log(`🎙️ Generating audio for chapter ${chapter.id} of "${parentItem.title}"`);

      await generateAudioForChapter(
        section,
        language,
        parentItem.id,
        chapter.id,
        NEVILLE_GODDARD_VOICE.id
      );

      // DON'T refresh entire screen - let real-time updates handle it
      console.log('🎯 Audio generation completed - no screen refresh needed');

      // Clear generating state
      setGeneratingAudioChapters(prev => {
        const newSet = new Set(prev);
        newSet.delete(chapterKey);
        return newSet;
      });

      console.log(`✅ Audio generation completed for chapter ${chapter.id}`);

    } catch (error) {
      console.error('❌ Error generating chapter audio:', error);
      setErrorMessage(`Failed to generate audio for Chapter ${chapter.id}: ${error.message}`);
      setTimeout(() => setErrorMessage(''), 5000);

      setGeneratingAudioChapters(prev => {
        const newSet = new Set(prev);
        newSet.delete(chapterKey);
        return newSet;
      });
    }
  };

  // Handle editing a chapter
  const startEditingChapter = (chapter, parentItem) => {
    const chapterText = Array.isArray(chapter.text) ? chapter.text : [chapter.text || ''];

    // Decide editing mode: use unified editor if no text or only empty text
    const hasNoMeaningfulText = chapterText.length === 0 || chapterText.every(t => !t || t.trim() === '');

    setEditingChapter({ ...chapter, parentId: parentItem.id });
    setUseUnifiedTextEditor(hasNoMeaningfulText);

    if (hasNoMeaningfulText) {
      // Start with unified text editor
      setEditChapterValues({
        title: chapter.title || '',
        text: [''] // Single empty string for unified editor
      });
    } else {
      // Use individual text editor
      setEditChapterValues({
        title: chapter.title || '',
        text: [...chapterText]
      });
    }

    console.log('🔧 Started editing chapter:', {
      chapterId: chapter.id,
      title: chapter.title,
      originalText: chapterText,
      unifiedMode: hasNoMeaningfulText
    });
  };

  const cancelEditingChapter = () => {
    setEditingChapter(null);
    setEditChapterValues({ title: '', text: [] });
    setEditingChapterLoading(false);
    setUseUnifiedTextEditor(false);
  };

  const handleEditChapter = async () => {
    if (!editChapterValues.title.trim()) {
      setErrorMessage('Chapter title is required');
      return;
    }

    try {
      setEditingChapterLoading(true);
      setErrorMessage('');

      // Handle text based on editing mode
      let textToSave;
      if (useUnifiedTextEditor) {
        // Parse unified text into array
        const unifiedText = editChapterValues.text[0] || '';
        textToSave = parseTextToArray(unifiedText);
      } else {
        // Use individual text entries, filter out empty ones
        textToSave = editChapterValues.text.filter(t => t.trim() !== '');
      }

      // Find the parent item
      const parentItem = content.find(item => item.id === editingChapter.parentId);
      if (!parentItem) {
        setErrorMessage('Parent content not found');
        return;
      }

      console.log('🔄 Updating chapter:', {
        chapterId: editingChapter.id,
        title: editChapterValues.title.trim(),
        textToSave,
        unifiedMode: useUnifiedTextEditor
      });

      // Update the chapter in subcollection
      await updateChapterInSubcollection(
        section,
        language,
        editingChapter.parentId,
        editingChapter.id,
        {
          title: editChapterValues.title.trim(),
          text: textToSave
          // Note: Existing audioURL is preserved, no regeneration on edit
        }
      );

      // Reset editing state
      cancelEditingChapter();

      // Refresh content
      await fetchContent();

      console.log('✅ Chapter updated successfully');

    } catch (error) {
      console.error('❌ Error updating chapter:', error);
      setErrorMessage('Failed to update chapter');
    } finally {
      setEditingChapterLoading(false);
    }
  };

  // Add/remove text elements in edit mode
  const addTextElement = () => {
    setEditChapterValues(prev => ({
      ...prev,
      text: [...prev.text, '']
    }));
  };

  const removeTextElement = (index) => {
    setEditChapterValues(prev => ({
      ...prev,
      text: prev.text.filter((_, i) => i !== index)
    }));
  };

  const updateTextElement = (index, value) => {
    setEditChapterValues(prev => ({
      ...prev,
      text: prev.text.map((text, i) => i === index ? value : text)
    }));
  };

  // TEST: Simple audio generation with few words
  const handleTestSimpleAudio = async (chapter, parentItem) => {
    try {
      console.log('🧪 Starting simple audio test for chapter:', chapter.id);

      const result = await testSimpleAudioGeneration(
        section, // Use actual section (lectures/books/podcasts)
        language || 'en',
        parentItem.id,
        chapter.id
      );

      if (result.success) {
        console.log('✅ Simple audio test completed:', result);
        alert(`🧪 TEST Success!\n\nAudio generated successfully: ${result.audioURL}\n\nCheck console for detailed response analysis`);
        // Refresh the content to show the new audio
        await fetchContent();
      } else {
        console.error('❌ Simple audio test failed:', result);
        alert(`❌ TEST Failed!\n\n${result.error}\n\nCheck console for response details`);
      }
    } catch (error) {
      console.error('💥 Test error:', error);
      alert(`💥 Test Error: ${error.message}`);
    }
  };

  // If an item is being edited, render the form in edit mode.
  if (editingItem) {
    return (
      <NewContentForm
        editingItem={editingItem}
        section={section}
        onFinish={() => {
          setEditingItem(null);
          fetchContent();
        }}
      />
    );
  }

  // --- Inner Components ---

  // AudioPlayer: Renders the audio player for podcasts.
  const AudioPlayer = ({ item }) => {
    let audioURL = item.audioURL;
    let audioDuration = item.audioDuration;
    let voiceUsed = item.voice || 'Default';
    if (!audioURL) {
      return <p>{i18n.t('noAudioAvailable')}</p>;
    }
    return (
      <div>
        <p>
          <strong>{i18n.t('duration')}:</strong> {audioDuration ? `${audioDuration.toFixed(2)} s` : 'N/A'}
        </p>
        <audio controls src={audioURL} style={{ width: '100%' }}></audio>
        <div style={{ marginTop: 10 }}>
          <p><strong>{i18n.t('voice')}:</strong> {voiceUsed}</p>
          <Button
            onClick={() => handleRegenerateAudio(item, { name: voiceUsed })}
            color="primary"
          >
            {generatingAudio === voiceUsed ? (
              <span className="spinner-border spinner-border-sm text-light"></span>
            ) : (
              i18n.t('regenerate')
            )}
          </Button>
        </div>
      </div>
    );
  };

  // Render podcasts block.
  const renderPodcastContent = (item) => {
    return (
      <>
        {item.text && item.text.map((paragraph, i) => (
          <p key={i}>{paragraph}</p>
        ))}
        <AudioPlayer item={item} />
      </>
    );
  };

    // Render chapters with modern, compact design and edit functionality
  const renderChapters = (chapters, parentItem) => {
    console.log(`🔍 Rendering ${chapters.length} chapters for "${parentItem.title}":`, chapters);

    if (!chapters || chapters.length === 0) {
      return (
        <div style={{ marginTop: 15, padding: 15, border: '2px dashed #e0e0e0', borderRadius: 8, textAlign: 'center' }}>
          <i className="bx bx-book-content" style={{ fontSize: 24, color: '#ccc', marginBottom: 8 }}></i>
          <p style={{ fontStyle: 'italic', color: '#888', margin: 0 }}>{i18n.t('noChaptersAvailable')}</p>
        </div>
      );
    }

    return (
      <div style={{ marginTop: 15 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 15,
          padding: '8px 12px',
          backgroundColor: '#f8f9fa',
          borderRadius: 6,
          border: '1px solid #e9ecef'
        }}>
          <i className="bx bx-book-content" style={{ fontSize: 18, color: '#495057', marginRight: 8 }}></i>
          <strong style={{ color: '#495057' }}>{i18n.t('chapters')} ({chapters.length})</strong>
        </div>

        <div style={{ display: 'grid', gap: 12 }}>
          {chapters.map((chapter) => {
            const isExpanded = expandedChapters[parentItem.id]?.[chapter.id] || false;
            const isEditing = editingChapter && editingChapter.id === chapter.id && editingChapter.parentId === parentItem.id;

            // Check if audio is generating for this chapter
            const chapterKey = `${parentItem.id}-${chapter.id}`;
            const isAudioGenerating = audioGeneratingChapters.has(chapterKey) || chapter.audioGenerating;
            const isManuallyGeneratingAudio = generatingAudioChapters.has(chapterKey);

            // Handle chapter text - could be array or string
            const chapterText = Array.isArray(chapter.text)
              ? chapter.text
              : (chapter.text ? [chapter.text] : []);

            if (isEditing) {
              return (
                <div
                  key={chapter.id}
                  style={{
                    border: '2px solid #007bff',
                    borderRadius: 8,
                    padding: 16,
                    backgroundColor: '#f8f9ff'
                  }}
                >
                  <div style={{ marginBottom: 12 }}>
                    <h6 style={{ color: '#007bff', margin: '0 0 12px 0', display: 'flex', alignItems: 'center' }}>
                      <i className="bx bx-edit" style={{ marginRight: 6 }}></i>
                      Editing Chapter {chapter.id}
                    </h6>
                  </div>

                  {/* Title Input */}
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: 4 }}>Title:</label>
                    <Input
                      type="text"
                      value={editChapterValues.title}
                      onChange={(e) => setEditChapterValues({ ...editChapterValues, title: e.target.value })}
                      placeholder="Chapter title"
                      style={{ fontSize: '14px' }}
                    />
                  </div>

                  {/* Audio Status Display */}
                  {chapter.audioURL && (
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: 4, color: '#28a745' }}>
                        <i className="bx bx-check-circle" style={{ marginRight: 4 }}></i>
                        Audio Available:
                      </label>
                      <div style={{
                        padding: '8px 12px',
                        backgroundColor: '#d4edda',
                        border: '1px solid #c3e6cb',
                        borderRadius: 6,
                        fontSize: '13px',
                        color: '#155724'
                      }}>
                        Generated with Neville Goddard voice
                        {chapter.audioDuration && (
                          <span style={{ marginLeft: 8, fontWeight: 'bold' }}>
                            ({Math.round(chapter.audioDuration)}s)
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Text Editor Mode Toggle */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <label style={{ fontSize: '14px', fontWeight: 'bold' }}>Text Editor Mode:</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                          color={!useUnifiedTextEditor ? "primary" : "outline-secondary"}
                          size="sm"
                          onClick={() => {
                            if (useUnifiedTextEditor) {
                              // Convert from unified to individual
                              const unifiedText = editChapterValues.text[0] || '';
                              const parsedArray = parseTextToArray(unifiedText);
                              setEditChapterValues(prev => ({ ...prev, text: parsedArray.length > 0 ? parsedArray : [''] }));
                              setUseUnifiedTextEditor(false);
                            }
                          }}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                        >
                          Individual Paragraphs
                        </Button>
                        <Button
                          color={useUnifiedTextEditor ? "primary" : "outline-secondary"}
                          size="sm"
                          onClick={() => {
                            if (!useUnifiedTextEditor) {
                              // Convert from individual to unified
                              const unifiedText = editChapterValues.text.join('\n\n');
                              setEditChapterValues(prev => ({ ...prev, text: [unifiedText] }));
                              setUseUnifiedTextEditor(true);
                            }
                          }}
                          style={{ fontSize: '12px', padding: '4px 8px' }}
                        >
                          Unified Text
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Text Editor */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: 8 }}>
                      {useUnifiedTextEditor ? 'Chapter Content:' : 'Text Paragraphs:'}
                    </label>

                    {useUnifiedTextEditor ? (
                      // Unified Text Editor (like when creating new chapters)
                      <div>
                        <Input
                          type="textarea"
                          value={editChapterValues.text[0] || ''}
                          onChange={(e) => setEditChapterValues(prev => ({ ...prev, text: [e.target.value] }))}
                          placeholder={`Enter chapter content...

Example:
• First point about manifestation
• Second important concept
• Third key principle

Or:

1. Introduction to the technique
2. Step-by-step process
3. Expected results

The text will be automatically split into separate paragraphs based on bullet points or numbers.`}
                          style={{
                            fontSize: '14px',
                            padding: '16px',
                            border: '2px solid #e9ecef',
                            borderRadius: 8,
                            minHeight: '150px',
                            lineHeight: 1.5,
                            fontFamily: 'inherit',
                            transition: 'border-color 0.2s'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#007bff'}
                          onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                        />

                        {/* Preview */}
                        {editChapterValues.text[0] && (
                          <div style={{
                            marginTop: 12,
                            padding: 12,
                            backgroundColor: '#ffffff',
                            border: '1px solid #e9ecef',
                            borderRadius: 6,
                            fontSize: '13px'
                          }}>
                            <strong style={{ color: '#495057' }}>Preview:</strong>
                            <span style={{ color: '#6c757d' }}> Will be split into </span>
                            <strong style={{ color: '#007bff' }}>{parseTextToArray(editChapterValues.text[0]).length}</strong>
                            <span style={{ color: '#6c757d' }}> paragraph(s)</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Individual Paragraph Editor
                      <div>
                        {editChapterValues.text.map((textItem, index) => (
                          <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                            <span style={{
                              fontSize: '12px',
                              color: '#666',
                              backgroundColor: '#e9ecef',
                              padding: '4px 8px',
                              borderRadius: 4,
                              minWidth: '24px',
                              textAlign: 'center',
                              marginTop: 4
                            }}>
                              {index + 1}
                            </span>
                            <Input
                              type="textarea"
                              value={textItem}
                              onChange={(e) => updateTextElement(index, e.target.value)}
                              placeholder={`Paragraph ${index + 1}...`}
                              style={{ fontSize: '14px', flex: 1, minHeight: '60px' }}
                            />
                            {editChapterValues.text.length > 1 && (
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => removeTextElement(index)}
                                style={{ marginTop: 4 }}
                              >
                                <i className="bx bx-x"></i>
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          color="success"
                          size="sm"
                          onClick={addTextElement}
                          style={{ marginTop: 8 }}
                        >
                          <i className="bx bx-plus"></i> Add Paragraph
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Edit Actions */}
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={cancelEditingChapter}
                      disabled={editingChapterLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleEditChapter}
                      disabled={editingChapterLoading || !editChapterValues.title.trim()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      {editingChapterLoading ? (
                        <>
                          <div className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }}></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="bx bx-save"></i>
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={chapter.id}
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 8,
                  backgroundColor: '#ffffff',
                  transition: 'all 0.2s ease',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                {/* Chapter Header */}
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: isExpanded ? '#f8f9fa' : '#ffffff',
                  borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: 12,
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {chapter.id}
                      </span>

                      {/* Access Level Badge - FREE for chapter 1, PRO for others */}
                      <span style={{
                        backgroundColor: chapter.accessLevel === 'FREE' || chapter.id === 1 ? '#28a745' : '#ffc107',
                        color: chapter.accessLevel === 'FREE' || chapter.id === 1 ? 'white' : '#000',
                        padding: '2px 6px',
                        borderRadius: 8,
                        fontSize: '10px',
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                      }}>
                        {chapter.accessLevel || (chapter.id === 1 ? 'FREE' : 'PRO')}
                      </span>

                      <h6 style={{ margin: 0, color: '#333', fontSize: '15px', fontWeight: '600' }}>
                        {chapter.title || `Chapter ${chapter.id}`}
                      </h6>

                      {/* Audio Status Indicators */}
                      {isAudioGenerating ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <div className="spinner-border spinner-border-sm" style={{
                            width: '12px',
                            height: '12px',
                            color: chapter.timeoutOccurred ? '#856404' : '#28a745'
                          }}></div>
                          <span style={{
                            fontSize: '11px',
                            color: chapter.timeoutOccurred ? '#856404' : '#28a745',
                            backgroundColor: chapter.timeoutOccurred ? '#fff3cd' : '#e8f5e8',
                            padding: '2px 6px',
                            borderRadius: 8,
                            fontWeight: 'bold'
                          }}>
                            {chapter.timeoutOccurred ? 'Processing (Slow)...' : 'Generating Audio...'}
                          </span>
                        </div>
                      ) : chapter.needsManualVerification ? (
                        <span style={{ fontSize: '11px', color: '#fd7e14', backgroundColor: '#fff3cd', padding: '2px 6px', borderRadius: 8, fontWeight: 'bold' }}>
                          ⚠️ Check Storage
                        </span>
                      ) : chapter.audioError && chapter.audioErrorMessage === 'deadline-exceeded' ? (
                        <span style={{ fontSize: '11px', color: '#856404', backgroundColor: '#fff3cd', padding: '2px 6px', borderRadius: 8, fontWeight: 'bold' }}>
                          Processing...
                        </span>
                      ) : chapter.audioError && chapter.audioErrorMessage !== 'deadline-exceeded' ? (
                        <span style={{ fontSize: '11px', color: '#dc3545', backgroundColor: '#f8d7da', padding: '2px 6px', borderRadius: 8, fontWeight: 'bold' }}>
                          Audio Error
                        </span>
                      ) : chapter.audioDuration ? (
                        <span style={{ fontSize: '11px', color: '#666', backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: 8 }}>
                          {Math.round(chapter.audioDuration)}s
                        </span>
                      ) : chapter.audioURL ? (
                        <span style={{ fontSize: '11px', color: '#28a745', backgroundColor: '#e8f5e8', padding: '2px 6px', borderRadius: 8 }}>
                          Audio Ready
                        </span>
                      ) : (
                        <span style={{ fontSize: '11px', color: '#6c757d', backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: 8 }}>
                          No Audio
                        </span>
                      )}
                    </div>
                    {!isExpanded && (
                      <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '13px', lineHeight: 1.3 }}>
                        {chapterText.length > 0
                          ? chapterText.join(' ').substring(0, 100) + '...'
                          : 'No preview available'}
                      </p>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {/* Generate Audio Button - Quick Access */}
                    {chapterText.length > 0 && (
                      <Button
                        color={chapter.audioURL ? "warning" : "success"}
                        size="sm"
                        onClick={() => handleGenerateChapterAudio(chapter, parentItem)}
                        disabled={isManuallyGeneratingAudio}
                        style={{
                          padding: '4px 8px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          borderRadius: 12,
                          minWidth: '70px'
                        }}
                        title={chapter.audioURL ? "Regenerate with smart chunking (optimal quality)" : "Generate with smart chunking (optimal quality)"}
                      >
                        {isManuallyGeneratingAudio ? (
                          <div className="spinner-border" style={{ width: '12px', height: '12px' }}></div>
                        ) : (
                          <>
                            <i className="bx bx-microphone" style={{ fontSize: '12px', marginRight: 2 }}></i>
                            {chapter.audioURL ? "Regen" : "Gen"}
                          </>
                        )}
                      </Button>
                    )}

                    {/* Simple Test Button - few words only */}
                    <Button
                      size="sm"
                      color="info"
                      onClick={() => handleTestSimpleAudio(chapter, parentItem)}
                      style={{
                        padding: '2px 6px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        borderRadius: 8,
                        marginLeft: '4px'
                      }}
                      title="TEST: Generate audio with just a few words to debug response handling"
                    >
                      🧪 Test
                    </Button>

                    <Button
                      color="link"
                      size="sm"
                      onClick={() => startEditingChapter(chapter, parentItem)}
                      style={{ padding: '4px 8px', color: '#6c757d' }}
                      title="Edit chapter"
                    >
                      <i className="bx bx-edit" style={{ fontSize: '16px' }}></i>
                    </Button>
                    <Button
                      color="link"
                      size="sm"
                      onClick={() => toggleChapterExpansion(parentItem.id, chapter.id)}
                      style={{ padding: '4px 8px', color: '#6c757d' }}
                    >
                      <i className={`bx ${isExpanded ? 'bx-chevron-up' : 'bx-chevron-down'}`} style={{ fontSize: '16px' }}></i>
                    </Button>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div style={{ padding: '16px' }}>
                    {/* Text Content */}
                    {chapterText.length > 0 ? (
                      <div style={{ marginBottom: 16 }}>
                        <h6 style={{ color: '#495057', fontSize: '14px', fontWeight: 'bold', marginBottom: 8 }}>Content:</h6>
                        <div style={{ maxHeight: '300px', overflowY: 'auto', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: 6 }}>
                          {chapterText.map((paragraph, i) => (
                            <div key={i} style={{
                              marginBottom: i === chapterText.length - 1 ? 0 : 12,
                              padding: '8px 12px',
                              backgroundColor: '#ffffff',
                              borderRadius: 4,
                              borderLeft: '3px solid #007bff',
                              fontSize: '14px',
                              lineHeight: 1.5
                            }}>
                              <span style={{ fontSize: '11px', color: '#666', fontWeight: 'bold' }}>¶{i + 1}</span>
                              <p style={{ margin: '4px 0 0 0' }}>{paragraph}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p style={{ fontStyle: 'italic', color: '#888', textAlign: 'center', margin: '16px 0' }}>No text content available</p>
                    )}

                    {/* Audio Player */}
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <h6 style={{ color: '#495057', fontSize: '14px', fontWeight: 'bold', margin: 0 }}>Audio:</h6>
                        {/* Generate Audio button */}
                        {chapterText.length > 0 && (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <Button
                              color={chapter.audioURL ? "warning" : "success"}
                              size="sm"
                              onClick={() => handleGenerateChapterAudio(chapter, parentItem)}
                              disabled={isManuallyGeneratingAudio || isAudioGenerating}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                borderRadius: 16,
                                border: 'none',
                                background: chapter.audioURL
                                  ? 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)'
                                  : 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                                boxShadow: chapter.audioURL
                                  ? '0 2px 6px rgba(255, 193, 7, 0.3)'
                                  : '0 2px 6px rgba(40, 167, 69, 0.3)',
                                color: 'white'
                              }}
                              title={chapter.audioURL ? "Regenerate audio" : "Generate audio"}
                            >
                              {isManuallyGeneratingAudio || isAudioGenerating ? (
                                <>
                                  <div className="spinner-border" style={{ width: '14px', height: '14px' }}></div>
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <i className="bx bx-microphone" style={{ fontSize: '14px' }}></i>
                                  {chapter.audioURL ? "🔄 Regenerate" : "🎵 Generate"}
                                </>
                              )}
                            </Button>

                            {/* Test button */}
                            <Button
                              color="info"
                              size="sm"
                              onClick={() => handleTestSimpleAudio(chapter, parentItem)}
                              style={{
                                padding: '4px 8px',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                borderRadius: 12
                              }}
                              title="TEST: Generate audio with just a few words to debug"
                            >
                              🧪 Test
                            </Button>
                          </div>
                        )}
                      </div>

                      {isAudioGenerating ? (
                        <div style={{
                          padding: '12px 16px',
                          backgroundColor: chapter.timeoutOccurred ? '#fff3cd' : '#e8f5e8',
                          border: chapter.timeoutOccurred ? '1px solid #ffeaa7' : '1px solid #c3e6cb',
                          borderRadius: 6,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8
                        }}>
                          <div className="spinner-border spinner-border-sm" style={{
                            width: '16px',
                            height: '16px',
                            color: chapter.timeoutOccurred ? '#856404' : '#28a745'
                          }}></div>
                          <div style={{ fontSize: '14px', color: chapter.timeoutOccurred ? '#856404' : '#155724' }}>
                            {chapter.timeoutOccurred ? (
                              <div>
                                <div style={{ fontWeight: 'bold' }}>TTS is taking longer than expected...</div>
                                <div style={{ fontSize: '12px', marginTop: 2 }}>
                                  Still processing in background. This can take up to 15 minutes for complex chapters.
                                </div>
                              </div>
                            ) : (
                              <div>
                                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                                  🎵 Generating audio with Neville Goddard's voice
                                </div>
                                {chapter.processingStatus && !chapter.processingStatus.includes('chunk') && (
                                  <div style={{ fontSize: '12px', fontStyle: 'italic' }}>
                                    {chapter.processingStatus}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : chapter.audioError && chapter.audioErrorMessage !== 'deadline-exceeded' ? (
                        <div style={{
                          padding: '12px 16px',
                          backgroundColor: '#f8d7da',
                          border: '1px solid #f5c6cb',
                          borderRadius: 6,
                          color: '#721c24',
                          fontSize: '14px'
                        }}>
                          ⚠️ Audio generation failed. {chapter.audioErrorMessage && `Error: ${chapter.audioErrorMessage}`}
                          <div style={{ fontSize: '12px', marginTop: 4, color: '#856404' }}>
                            💡 Try the "Generate Audio" button above to retry
                          </div>
                        </div>

                      ) : chapter.needsManualVerification ? (
                        <div style={{
                          padding: '16px',
                          backgroundColor: '#fff3cd',
                          border: '2px solid #fd7e14',
                          borderRadius: 8,
                          color: '#856404',
                          fontSize: '14px'
                        }}>
                          <div style={{ fontWeight: 'bold', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            ⚠️ Manual Verification Required
                          </div>
                          <div style={{ marginBottom: 12, fontSize: '13px', lineHeight: 1.4 }}>
                            ElevenLabs likely generated the audio successfully, but Firebase had an internal error returning the URL.
                          </div>

                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontWeight: 'bold', marginBottom: 4, fontSize: '13px' }}>📍 Check Firebase Storage:</div>
                            {chapter.consoleURL && (
                              <a
                                href={chapter.consoleURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: '#fd7e14',
                                  textDecoration: 'none',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  display: 'inline-block',
                                  padding: '4px 8px',
                                  backgroundColor: 'rgba(253, 126, 20, 0.1)',
                                  borderRadius: 4,
                                  border: '1px solid rgba(253, 126, 20, 0.3)'
                                }}
                              >
                                🔗 Open Firebase Console
                              </a>
                            )}
                          </div>

                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontWeight: 'bold', marginBottom: 4, fontSize: '13px' }}>📂 Look for file:</div>
                            <code style={{
                              backgroundColor: 'rgba(0,0,0,0.1)',
                              padding: '4px 6px',
                              borderRadius: 4,
                              fontSize: '11px',
                              wordBreak: 'break-all'
                            }}>
                              {chapter.expectedStoragePath}
                            </code>
                          </div>

                          <div style={{ marginBottom: 12, fontSize: '12px', fontStyle: 'italic', color: '#6c757d' }}>
                            If the audio file exists, copy its download URL and use the browser console:
                            <br />
                            <code style={{ fontSize: '11px' }}>
                              window.nevilizeHelpers.manuallyUpdateChapterAudio("${section}", "${language}", "${parentItem.id}", "${chapter.id}", "YOUR_AUDIO_URL")
                            </code>
                          </div>

                          {chapter.processingStatus && (
                            <div style={{
                              fontSize: '12px',
                              color: '#6c757d',
                              marginTop: 8,
                              padding: '6px 8px',
                              backgroundColor: 'rgba(0,0,0,0.05)',
                              borderRadius: 4
                            }}>
                              Status: {chapter.processingStatus}
                            </div>
                          )}
                        </div>

                      ) : chapter.audioURL ? (
                        <div>
                          <audio controls src={chapter.audioURL} style={{ width: '100%', height: '40px', marginBottom: 8 }}>
                            Your browser does not support the audio element.
                          </audio>
                          <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#d4edda',
                            border: '1px solid #c3e6cb',
                            borderRadius: 6,
                            fontSize: '12px',
                            color: '#155724',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontWeight: 'bold', marginBottom: 2 }}>
                              ✅ Audio Ready
                              {chapter.chunkCount > 1 && (
                                <span style={{ marginLeft: 6, fontSize: '11px' }}>
                                  🧩 Merged from {chapter.chunkCount} chunks
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '11px', opacity: 0.8 }}>
                               {chapter.processingMethod === 'multi-chunk-parallel'
                                 ? `🚀 Complete: ${chapter.chunkCount} chunks in parallel (${chapter.optimizedTextLength} chars, ${Math.round(chapter.audioDuration || 0)}s)`
                                 : chapter.processingMethod === 'single-chunk'
                                 ? 'Generated as single optimized chunk (complete)'
                                 : 'Use "Generate" button to create audio with smart chunking'
                               }
                             </div>
                          </div>
                        </div>
                      ) : chapterText.length > 0 ? (
                        <div style={{
                          padding: '12px 16px',
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #dee2e6',
                          borderRadius: 6,
                          color: '#6c757d',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}>
                          📝 Text content ready • Use "Generate" button above to create audio with smart chunking for optimal quality
                        </div>
                      ) : (
                        <div style={{
                          padding: '12px 16px',
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #dee2e6',
                          borderRadius: 6,
                          color: '#6c757d',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}>
                          No text content available - cannot generate audio
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render lectures/books block.
  const renderLectureBookContent = (item) => {
    return (
      <>
        {item.author && (
          <p>
            <strong>{i18n.t('by')}:</strong> {item.author}
          </p>
        )}
        {item.title && (
          <p>
            <strong>{i18n.t('title')}:</strong> {item.title}
          </p>
        )}
        {Array.isArray(item.chapters) && item.chapters.length > 0
          ? renderChapters(item.chapters, item)
          : <p>{i18n.t('noChaptersAvailable')}</p>}
        {/* Action Buttons */}
        {chapterFormItemId !== item.id && (
          <div style={{ marginTop: 20, textAlign: 'center', display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {/* Add New Chapter Button */}
            <Button
              color="success"
              size="lg"
              onClick={() => setChapterFormItemId(item.id)}
              style={{
                padding: '12px 24px',
                borderRadius: 25,
                fontWeight: '600',
                fontSize: '15px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                boxShadow: '0 3px 10px rgba(40, 167, 69, 0.2)',
                border: 'none',
                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 5px 15px rgba(40, 167, 69, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 3px 10px rgba(40, 167, 69, 0.2)';
              }}
            >
              <i className="bx bx-plus-circle" style={{ fontSize: '18px' }}></i>
              Add New Chapter
            </Button>

            {/* Generate Missing Audio Button */}
            <Button
              color="warning"
              size="lg"
              onClick={() => handleGenerateMissingAudio(item)}
              disabled={generatingBulkAudio === item.id}
              style={{
                padding: '12px 24px',
                borderRadius: 25,
                fontWeight: '600',
                fontSize: '15px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                boxShadow: '0 3px 10px rgba(255, 193, 7, 0.2)',
                border: 'none',
                background: generatingBulkAudio === item.id
                  ? 'linear-gradient(135deg, #6c757d 0%, #495057 100%)'
                  : 'linear-gradient(135deg, #ffc107 0%, #ff8c00 100%)',
                transition: 'all 0.3s ease',
                color: '#000'
              }}
              onMouseEnter={(e) => {
                if (generatingBulkAudio !== item.id) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 5px 15px rgba(255, 193, 7, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (generatingBulkAudio !== item.id) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 3px 10px rgba(255, 193, 7, 0.2)';
                }
              }}
            >
              {generatingBulkAudio === item.id ? (
                <>
                  <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '18px' }}></i>
                  Generating Audio...
                </>
              ) : (
                <>
                  <i className="bx bx-microphone" style={{ fontSize: '18px' }}></i>
                  Generate Missing Audio
                </>
              )}
            </Button>
          </div>
        )}
        {/* Modern Add Chapter Form */}
        {chapterFormItemId === item.id && (
          <div
            style={{
              marginTop: 20,
              padding: 20,
              border: '2px solid #28a745',
              borderRadius: 12,
              backgroundColor: '#f8fff9',
              boxShadow: '0 4px 12px rgba(40, 167, 69, 0.15)'
            }}
          >
            <div style={{ marginBottom: 16 }}>
              <h5 style={{ color: '#28a745', margin: '0 0 8px 0', display: 'flex', alignItems: 'center' }}>
                <i className="bx bx-plus-circle" style={{ marginRight: 8, fontSize: '20px' }}></i>
                Add New Chapter
              </h5>
              <p style={{ color: '#6c757d', fontSize: '14px', margin: 0 }}>
                Create a new chapter with automatic text formatting detection
              </p>
            </div>

            {/* Title Input */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: 6, color: '#495057' }}>
                Chapter Title *
              </label>
              <Input
                type="text"
                value={chapterFormValues.title}
                onChange={(e) => setChapterFormValues({ ...chapterFormValues, title: e.target.value })}
                placeholder="Enter chapter title..."
                style={{
                  fontSize: '15px',
                  padding: '12px',
                  border: '2px solid #e9ecef',
                  borderRadius: 8,
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#28a745'}
                onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
              />
            </div>

                        {/* Audio Generation Notice */}
            <div style={{ marginBottom: 16 }}>
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#e8f5e8',
                border: '1px solid #c3e6cb',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}>
                <div style={{
                  backgroundColor: '#28a745',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <i className="bx bx-microphone" style={{ color: 'white', fontSize: '16px' }}></i>
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#155724', marginBottom: 2 }}>
                    🎙️ Automatic Audio Generation
                  </div>
                  <div style={{ fontSize: '13px', color: '#155724' }}>
                    Audio will be automatically generated using <strong>Neville Goddard's voice</strong> after creating the chapter
                  </div>
                </div>
              </div>
            </div>

            {/* Text Content Input */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', marginBottom: 6, color: '#495057' }}>
                Chapter Content *
              </label>
              <div style={{ marginBottom: 8 }}>
                <small style={{ color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                  💡 Tip: Paste text with bullet points (•, -, *) or numbered lists (1., 2., 3.) and they'll be automatically separated into paragraphs!
                </small>
              </div>
              <Input
                type="textarea"
                value={chapterFormValues.text}
                onChange={(e) => {
                  console.log('📝 Text input changing:', { from: chapterFormValues.text, to: e.target.value });
                  setChapterFormValues({ ...chapterFormValues, text: e.target.value });
                }}
                onPaste={(e) => {
                  // Allow the paste to complete first
                  setTimeout(() => {
                    const pastedText = e.target.value;
                    if (pastedText && (pastedText.includes('•') || pastedText.includes('-') || pastedText.includes('*') || /\d+\./.test(pastedText))) {
                      const parsed = parseTextToArray(pastedText);
                      if (parsed.length > 1) {
                        // Show preview of how it will be parsed
                        console.log('📝 Text will be parsed into', parsed.length, 'paragraphs:', parsed);
                      }
                    }
                  }, 100);
                }}
                placeholder={`Enter chapter content...

Example:
• First point about manifestation
• Second important concept
• Third key principle

Or:

1. Introduction to the technique
2. Step-by-step process
3. Expected results

The text will be automatically split into separate paragraphs based on bullet points or numbers.`}
                style={{
                  fontSize: '14px',
                  padding: '16px',
                  border: '2px solid #e9ecef',
                  borderRadius: 8,
                  minHeight: '150px',
                  lineHeight: 1.5,
                  fontFamily: 'inherit',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#28a745'}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e9ecef';
                  if (chapterFormValues.text) {
                    console.log('📝 Current text value on blur:', chapterFormValues.text);
                    const testParse = parseTextToArray(chapterFormValues.text);
                    console.log('🧪 Test parse result:', testParse);
                  }
                }}
              />

              {/* Text Preview */}
              {chapterFormValues.text && (
                <div style={{
                  marginTop: 12,
                  padding: 12,
                  backgroundColor: '#ffffff',
                  border: '1px solid #e9ecef',
                  borderRadius: 6,
                  fontSize: '13px'
                }}>
                  <strong style={{ color: '#495057' }}>Preview:</strong>
                  <span style={{ color: '#6c757d' }}> Will be split into </span>
                  <strong style={{ color: '#28a745' }}>{parseTextToArray(chapterFormValues.text).length}</strong>
                  <span style={{ color: '#6c757d' }}> paragraph(s)</span>
                  {parseTextToArray(chapterFormValues.text).length > 1 && (
                    <div style={{ marginTop: 8, fontSize: '12px' }}>
                      {parseTextToArray(chapterFormValues.text).map((text, i) => (
                        <div key={i} style={{
                          marginBottom: 4,
                          padding: '4px 8px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: 4,
                          borderLeft: '2px solid #28a745'
                        }}>
                          <span style={{ fontWeight: 'bold', color: '#495057' }}>¶{i + 1}:</span> {text.substring(0, 80)}{text.length > 80 ? '...' : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 12, borderTop: '1px solid #e9ecef' }}>
              <Button
                color="secondary"
                onClick={() => {
                  setChapterFormItemId(null);
                  setChapterFormValues({ title: '', text: '' });
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  fontWeight: '600'
                }}
              >
                Cancel
              </Button>
              <Button
                color="success"
                onClick={() => handleAddChapter(item)}
                disabled={creatingChapter || !chapterFormValues.title.trim() || !chapterFormValues.text.trim()}
                style={{
                  padding: '10px 24px',
                  borderRadius: 8,
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                {creatingChapter ? (
                  <>
                    <div className="spinner-border spinner-border-sm"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="bx bx-plus"></i>
                    Create Chapter
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
        {/* Enhanced Translation Section */}
        <div style={{ marginTop: 20, padding: 16, backgroundColor: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <i className="bx bx-globe" style={{ fontSize: '18px', color: '#6c757d', marginRight: 8 }}></i>
            <strong style={{ color: '#495057' }}>{i18n.t('translateTo')}:</strong>
                         {section !== 'podcasts' && (
               <small style={{ marginLeft: 8, color: '#6c757d', fontStyle: 'italic' }}>
                 ({i18n.t('includesChaptersAndAudio')})
               </small>
             )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {appLanguages.filter(lang => lang !== language).map(targetLang => {
              const key = `${item.id}-${targetLang}`;
              const isTranslating = translating[key];

              return (
                <Button
                  key={targetLang}
                  color={isTranslating ? "primary" : "outline-secondary"}
                  size="sm"
                  onClick={() => handleTranslate(item, targetLang)}
                  disabled={isTranslating}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    minWidth: '80px',
                    padding: '6px 12px'
                  }}
                >
                  {isTranslating ? (
                    <>
                      <div className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }}></div>
                                             <span style={{ fontSize: '11px' }}>
                         {section === 'podcasts' ? i18n.t('translating') + '...' : i18n.t('translatingWithTTS') + '...'}
                       </span>
                    </>
                  ) : (
                    <>
                      <img
                        src={langFlags[targetLang]}
                        alt={`${targetLang} flag`}
                        style={{ width: 16, height: 16 }}
                      />
                      <span style={{ fontWeight: 600 }}>{targetLang.toUpperCase()}</span>
                    </>
                  )}
                </Button>
              );
            })}
          </div>

          {Object.values(translating).some(Boolean) && (
            <div style={{
              marginTop: 12,
              padding: 10,
              backgroundColor: '#d1ecf1',
              border: '1px solid #bee5eb',
              borderRadius: 6,
              fontSize: '13px',
              color: '#0c5460'
            }}>
                             <i className="bx bx-info-circle" style={{ marginRight: 6 }}></i>
               <strong>{i18n.t('translationInProgress')}:</strong> {i18n.t('translationIncludesChaptersAndAudio')}
            </div>
          )}
        </div>
      </>
    );
  };

  // Render content body depending on the section.
  const renderContentBody = (item) => {
    if (section === 'podcasts') {
      return renderPodcastContent(item);
    } else {
      return renderLectureBookContent(item);
    }
  };

  return (
    <div>
      {/* Audio Generation Notification */}
      {audioGeneratingChapters.size > 0 && (
        <div style={{
          marginBottom: 16,
          padding: '12px 16px',
          backgroundColor: '#e8f5e8',
          border: '1px solid #c3e6cb',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <div className="spinner-border spinner-border-sm" style={{ color: '#28a745' }}></div>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#155724' }}>
              🎙️ Audio Generation in Progress
            </div>
            <div style={{ fontSize: '13px', color: '#155724' }}>
              {audioGeneratingChapters.size} chapter{audioGeneratingChapters.size > 1 ? 's' : ''} generating audio with Neville Goddard's voice...
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <p>{i18n.t('loading')}...</p>
        </div>
      ) : content.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <p>{i18n.t('noContentAvailable')}</p>
        </div>
      ) : (
        content.map((item, index) => (
          <Card key={item.id} style={{ marginBottom: 15 }}>
            <CardHeader
              onClick={() => toggleCollapse(index)}
              style={{
                cursor: 'pointer',
                backgroundColor: '#f7f7f7',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div>
                <span style={{ fontWeight: 'bold' }}>{item.id}. </span>
                {item.title}
                {section !== 'podcasts' && item.author && (
                  <span style={{ marginLeft: 10, fontStyle: 'italic', color: '#555' }}>
                    {i18n.t('by')} {item.author}
                  </span>
                )}
              </div>
              <ButtonGroup size="sm">
                <Button
                  color="info"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingItem(item);
                  }}
                >
                  {i18n.t('edit')}
                </Button>
                <Button
                  color="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item);
                  }}
                >
                  {i18n.t('delete')}
                </Button>
              </ButtonGroup>
            </CardHeader>
            <Collapse isOpen={collapse[index]}>
              <CardBody>
                {renderContentBody(item)}
                <Button
                  color="primary"
                  onClick={fetchContent}
                  style={{ marginTop: 10 }}
                >
                  {i18n.t('refresh')}
                </Button>
                {errorMessage && (
                  <div className="error-message" style={{ color: 'red', marginTop: 10 }}>
                    {errorMessage}
                  </div>
                )}
              </CardBody>
            </Collapse>
          </Card>
        ))
      )}
    </div>
  );
};

export default ContentTable;
